/**
 * Sending somebody to another panel on the same screen.
 *
 * Lifted out of `Objectives.tsx` when the research screen needed the same jump (F-129): a
 * subject that found nothing offers the page from the learner's own book as one of its two
 * remedies, and that upload control is `Sources`, several panels below. A second copy of this
 * function would have been three lines and would have drifted the first time one of them was
 * fixed — this one has already been fixed twice, once for focus and once for a collapsed
 * `<details>`, and both fixes are the comments below.
 *
 * It is not in the test-compiled set and cannot be: it is nothing but DOM.
 */
import { MATERIAL_PANEL_ID } from './Sources';

/**
 * Scrolls and moves focus.
 *
 * Not exported. `goToUpload` is its only caller and the only jump anything asks for; an exported
 * helper whose callers are all in its own file is surface nobody is maintaining.
 *
 * Focus moves with the scroll deliberately. A button that only moves the viewport leaves a
 * keyboard or screen-reader user exactly where they were, having apparently pressed something
 * that did nothing.
 */
function goToPanel(id: string): void {
  const panel = document.getElementById(id);
  if (!panel) return;
  /*
   * The section is opened first, and without this the button silently does nothing.
   *
   * A closed `<details>` hides its contents with `display: none`, and an element that is not
   * rendered cannot be scrolled to and cannot take focus — both calls below are accepted and
   * discarded. Sections fold now, and the learner's choice is remembered, so "add a page"
   * pressed by somebody who collapsed the uploader last week would be a button that does nothing
   * at all with nothing anywhere saying why.
   *
   * Setting the attribute is enough to keep React in step: the browser fires `toggle`, which is
   * the same event a tap on the summary fires, so `Disclosure` records the section as open
   * exactly as it would have.
   */
  panel.closest('details')?.setAttribute('open', '');
  panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  panel.focus({ preventScroll: true });
}

/**
 * Sends the learner to the uploader instead of putting a second file control beside whatever
 * asked for a page.
 *
 * `Sources` owns presigning, the content-type fallback for phone browsers that report none, and
 * the HEIC rule that is the only reason photographing a page from an iPhone works at all. A
 * second upload button beside the gaps — or beside a barren subject — would be a second copy of
 * all of it, and the copies would drift the first time one of them was fixed.
 */
export function goToUpload(): void {
  goToPanel(MATERIAL_PANEL_ID);
}
